import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Collapse, Divider, LinearProgress, Toolbar } from '@mui/material';

import { useAppSelector } from '../../hooks';
import { getUrlConvertedString } from '../../helper';
import {
  getDiagnosticById,
  getDiagnosticState,
} from '../../store/DiagnosticSlice';
import {
  createSession,
  getStripeState,
  getSubscriptionSessionById,
} from '../../store/StripeSlice';
import {
  getGroupedSubTopics,
  getSubTopicState,
} from '../../store/SubTopicSlice';

import NavigationBarHeader from '../UserPages/UserDashboard/components/NavigationBarHeader';
import ProcessingView from './ProcessingView';
import ErrorView from './ErrorView';
import SuccessView from './SuccessView';

interface IState {
  reference: string;
  title: string;
}

const initialState: IState = {
  reference: '',
  title: '',
};

const Payment: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  // Store
  const diagnosticState = useAppSelector(getDiagnosticState);
  const subTopicState = useAppSelector(getSubTopicState);
  const stripeState = useAppSelector(getStripeState);

  const queryStringParams = new URLSearchParams(location.search);
  const diagnosticId = queryStringParams.get('diagnosticId') || '';
  const subscriptionId = queryStringParams.get('subscriptionId') || '';

  const { diagnostic } = diagnosticState;
  const { groupedTopicsWithSubTopicsData } = subTopicState;
  const { subscriptionSession } = stripeState;

  const [isSuccessScreenEnabled, setSuccessScreenEnabled] = useState(false);
  const [isErrorScreenEnabled, setErrorScreenEnabled] = useState(false);
  const [state, setState] = useState(initialState);

  useEffect(() => {
    if (diagnosticId) {
      setTimeout(() => {
        dispatch(getDiagnosticById(diagnosticId || ''));
      }, 5000);
    }

    if (subscriptionId) {
      setTimeout(() => {
        dispatch(getSubscriptionSessionById(subscriptionId || ''));
      }, 5000);
    }
  }, []);

  useEffect(() => {
    if (diagnostic) {
      if (
        diagnostic.stripeSession &&
        diagnostic.stripeSession.status === 'paid'
      ) {
        setSuccessScreenEnabled(true);
        setState({
          reference: diagnostic.stripeSession.id,
          title: `${diagnostic.recommendedHours || 0} hours`,
        });
      } else {
        setErrorScreenEnabled(true);
      }

      if (diagnostic.subTopicId) {
        dispatch(getGroupedSubTopics({ subTopicId: diagnostic.subTopicId }));
      }
    }
  }, [diagnostic]);

  useEffect(() => {
    if (subscriptionSession) {
      if (subscriptionSession.status === 'paid') {
        setSuccessScreenEnabled(true);
        const title = `${subscriptionSession.intervalCount} ${
          subscriptionSession.interval
        }${
          subscriptionSession.intervalCount &&
          subscriptionSession.intervalCount > 1
            ? 's'
            : ''
        }`;
        setState({
          reference:
            subscriptionSession?.subscriptionId ||
            subscriptionSession.sessionId,
          title,
        });
      } else {
        setErrorScreenEnabled(true);
      }
    }
  }, [subscriptionSession]);

  const handleTryAgain = () => {
    if (diagnosticId) {
      dispatch(
        createSession({
          diagnosticId,
        })
      );
    } else if (
      subscriptionId &&
      subscriptionSession &&
      subscriptionSession.url
    ) {
      window.location.href = subscriptionSession.url;
    }
  };

  const RENDER_CONTENT = () => {
    const curriculum = getUrlConvertedString(
      groupedTopicsWithSubTopicsData?.curriculum || ''
    );
    const subject = getUrlConvertedString(
      groupedTopicsWithSubTopicsData?.subject || ''
    );
    const subjectLevel = getUrlConvertedString(
      groupedTopicsWithSubTopicsData?.subjectLevel || ''
    );
    const subjectCode = getUrlConvertedString(
      groupedTopicsWithSubTopicsData?.subjectCode || ''
    );

    let homeURL = `${process.env.PUBLIC_URL}`;

    if (curriculum && subject && subjectLevel) {
      homeURL = `${process.env.PUBLIC_URL}/dashboard/${curriculum}/${subject}/${subjectLevel}/${subjectCode}`;
    }

    if (isSuccessScreenEnabled) {
      return (
        <SuccessView
          reference={state.reference}
          title={state.title}
          homeURL={homeURL}
        />
      );
    }

    if (isErrorScreenEnabled) {
      return <ErrorView retry={handleTryAgain} homeURL={homeURL} />;
    }

    return <ProcessingView />;
  };

  return (
    <div>
      <NavigationBarHeader />
      <Toolbar />
      <Collapse
        in={!isSuccessScreenEnabled}
        sx={{
          width: '100%',
          position: 'fixed',
          top: { xs: '56px', sm: '64px' },
        }}
      >
        {isErrorScreenEnabled ? (
          <Divider sx={{ height: '4px', backgroundColor: '#FF5630' }} />
        ) : (
          <LinearProgress />
        )}
      </Collapse>
      {RENDER_CONTENT()}
    </div>
  );
};

export default Payment;
